import { apiClient } from '../client';
import {
  Report,
  ReportDetails,
  UserBanHistory,
  UpdateReportStatusRequest,
  BanUserRequest,
  UnbanUserRequest,
  BulkUpdateReportsRequest,
  ReportFilters,
  BannedUser,
} from '../../types/report';

export const reportService = {
  // Get all reports with filters
  getReports: async (filters?: ReportFilters): Promise<{
    reports: Report[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const params = new URLSearchParams();

    if (filters?.status) params.append('status', filters.status);
    if (filters?.report_type) params.append('report_type', filters.report_type);
    if (filters?.school_id) params.append('school_id', filters.school_id);
    if (filters?.campus_id) params.append('campus_id', filters.campus_id);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`/api/admin/reports?${params.toString()}`);

    return {
      reports: response.data.reports || [],
      pagination: response.data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
      },
    };
  },

  // Get report details
  getReportDetails: async (reportId: string): Promise<ReportDetails> => {
    const response = await apiClient.get(`/api/admin/reports/${reportId}`);
    return response.data;
  },

  // Update report status
  updateReportStatus: async (
    reportId: string,
    data: UpdateReportStatusRequest
  ): Promise<Report> => {
    const response = await apiClient.put(`/api/admin/reports/${reportId}/status`, data);
    return response.data.report;
  },

  // Ban user from report
  banUserFromReport: async (reportId: string, data: BanUserRequest) => {
    const response = await apiClient.post(`/api/admin/reports/${reportId}/ban-user`, data);
    return response.data;
  },

  // Bulk update reports
  bulkUpdateReports: async (data: BulkUpdateReportsRequest) => {
    const response = await apiClient.put(`/api/admin/reports/bulk-update`, data);
    return response.data;
  },

  // Get banned users
  getBannedUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{
    users: BannedUser[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await apiClient.get(`/api/admin/users/banned?${queryParams.toString()}`);

    return {
      users: response.data.users || [],
      pagination: response.data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
      },
    };
  },

  // Unban user
  unbanUser: async (userId: string, data?: UnbanUserRequest) => {
    const response = await apiClient.post(`/api/admin/users/${userId}/unban`, data || {});
    return response.data;
  },

  // Get user ban history
  getUserBanHistory: async (userId: string): Promise<UserBanHistory> => {
    const response = await apiClient.get(`/api/admin/users/${userId}/ban-history`);
    return response.data;
  },

  // Remove user (existing functionality)
  removeUser: async (userId: string, reason: string, sendEmail = true) => {
    const response = await apiClient.delete(`/api/admin/users/${userId}/remove`, {
      data: { reason, sendEmail },
    });
    return response.data;
  },
};
